/**
 * The dwelling being surveyed.
 *
 * `PreviousHeatingFuel` exists for one reason: the BUS off gas grid uplift
 * (Ofgem Installer Guidance V5.1 §3.48) pays the extra £9,000 only when the
 * heating system being replaced was fuelled by oil or LPG. `packages/bus`
 * gates the uplift on exactly this fact — `BusPreviousHeatingFuel` in
 * `packages/bus/src/types.ts` — and until this field existed anywhere upstream
 * of that package, nothing ever supplied it, so the uplift was withheld on
 * every real application regardless of what was actually being replaced.
 *
 * The five fuel values below are deliberately identical, member for member, to
 * `BusPreviousHeatingFuel`. The two types are not the same TypeScript type —
 * `@sts/bus` is not a dependency of `@sts/schemas` or of the API, and adding
 * one just to share five string literals would wire a survey-capture package
 * to a grant-eligibility package for no benefit either side needs yet — but
 * they must stay in sync by hand. A value accepted here that
 * `assessEligibility` cannot recognise would surface as a silent "fuel not
 * confirmed" on every application for that property, which is exactly the
 * failure this field was added to close.
 *
 * `unknown-or-absent` is a sixth value with no counterpart in
 * `BusPreviousHeatingFuel`. It exists to distinguish two situations that would
 * otherwise both be recorded as "nothing here": a surveyor who asked and could
 * not get a confirmed answer (or found no previous heating system at all — a
 * new build, say), versus a record where the question was simply never put to
 * anyone. Both currently withhold the uplift identically, but only the first
 * is evidence, on the file, that due diligence was done — which is worth
 * keeping distinct from a blank field even though the eligibility engine does
 * not yet care about the difference.
 */
export type PreviousHeatingFuel =
  | "oil"
  | "lpg"
  | "mains-gas"
  | "electric"
  | "other"
  | "unknown-or-absent";

/**
 * Every value, as a runtime array for validating input at the API boundary —
 * the same treatment `JOB_STAGES` gets in `job.ts`, for the same reason: a
 * value the type system would reject at compile time still arrives over HTTP
 * as an untyped string, and has to be checked at runtime too.
 */
export const PREVIOUS_HEATING_FUELS: readonly PreviousHeatingFuel[] = [
  "oil",
  "lpg",
  "mains-gas",
  "electric",
  "other",
  "unknown-or-absent",
];
